import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Briefcase, Building, TrendingUp, ArrowUpRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { RFQCalendar } from '../components/ui/calendar-rfq';
import { useCandidates } from '../hooks/useCandidates';
import { useRFQs } from '../hooks/useRFQs';
import { useClients } from '../hooks/useClients';

const RecruiterDashboard = () => {
  const navigate = useNavigate();
  const { candidates, loading: candidatesLoading } = useCandidates();
  const { rfqs, loading: rfqsLoading } = useRFQs();
  const { clients, loading: clientsLoading } = useClients();

  const stats = [
    {
      title: 'Total Candidates',
      value: candidates.length,
      icon: Users,
      link: '/recruiter/candidates',
      trend: '+12% from last month',
    },
    {
      title: 'Active RFQs',
      value: rfqs.filter(r => ['in_progress', 'invited'].includes(r.status)).length,
      icon: Briefcase,
      link: '/recruiter/rfqs',
      trend: `${rfqs.filter(r => ['in_progress', 'invited'].includes(r.status)).length} of ${rfqs.length} total`,
    },
    {
      title: 'Total Clients',
      value: clients.length,
      icon: Building,
      link: '/recruiter/clients',
      trend: '+3 this month',
    },
    {
      title: 'Total RFQs',
      value: rfqs.length,
      icon: TrendingUp,
      link: '/recruiter/rfqs',
      trend: `${rfqs.filter(r => r.status === 'completed').length} completed`,
    },
  ];

  const loading = candidatesLoading || rfqsLoading || clientsLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1 text-sm sm:text-base">
            Welcome back! Here's an overview of your recruitment activity.
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <Card
            key={stat.title}
            className="cursor-pointer hover:bg-accent/50 transition-colors"
            onClick={() => navigate(stat.link)}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {stat.title}
              </CardTitle>
              <stat.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stat.value}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {stat.trend}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* RFQ Calendar */}
      <RFQCalendar
        rfqs={rfqs}
        onRFQClick={(rfq) => navigate(`/recruiter/rfqs/${rfq.id}`)}
      />

      {/* Recent Activity */}
      <div className="grid gap-3 sm:gap-4 grid-cols-1 md:grid-cols-2">
        {/* Recent Candidates */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Recent Candidates</CardTitle>
                <CardDescription className="mt-1.5">
                  Latest candidates added to the system
                </CardDescription>
              </div>
              <ArrowUpRight
                className="h-4 w-4 text-muted-foreground cursor-pointer hover:text-foreground"
                onClick={() => navigate('/recruiter/candidates')}
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {candidates.slice(0, 5).map((candidate) => (
                <div
                  key={candidate.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent cursor-pointer transition-colors"
                  onClick={() => navigate(`/recruiter/candidates/${candidate.id}`)}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium">
                      {candidate.first_name?.[0]}{candidate.last_name?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">
                        {candidate.first_name} {candidate.last_name}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{candidate.email}</p>
                    </div>
                  </div>
                  <Badge variant="secondary" className="ml-2">
                    {candidate.clearance_level || 'None'}
                  </Badge>
                </div>
              ))}
              {candidates.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Users className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No candidates yet</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent RFQs */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Recent RFQs</CardTitle>
                <CardDescription className="mt-1.5">
                  Latest requests for quotation
                </CardDescription>
              </div>
              <ArrowUpRight
                className="h-4 w-4 text-muted-foreground cursor-pointer hover:text-foreground"
                onClick={() => navigate('/recruiter/rfqs')}
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {rfqs.slice(0, 5).map((rfq) => (
                <div
                  key={rfq.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-card hover:bg-accent cursor-pointer transition-colors"
                  onClick={() => navigate(`/recruiter/rfqs/${rfq.id}`)}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted text-sm font-medium">
                      <Briefcase className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{rfq.ref_code}</p>
                      <p className="text-xs text-muted-foreground truncate">{rfq.title}</p>
                    </div>
                  </div>
                  <Badge
                    variant={
                      rfq.status === 'completed' ? 'default' :
                      rfq.status === 'in_progress' ? 'secondary' :
                      'outline'
                    }
                    className="ml-2"
                  >
                    {rfq.status}
                  </Badge>
                </div>
              ))}
              {rfqs.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <Briefcase className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No RFQs yet</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RecruiterDashboard;
